import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  permission?: keyof import('../../hooks/usePermissionCheck').UserPermission;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

export default function ProtectedRoute({ 
  children, 
  permission, // eslint-disable-line @typescript-eslint/no-unused-vars
  redirectTo, // eslint-disable-line @typescript-eslint/no-unused-vars
  fallback // eslint-disable-line @typescript-eslint/no-unused-vars
}: ProtectedRouteProps) {
  const { user } = useAuth();

  // SIMPLIFIED APPROACH: Allow access for authenticated users
  // Let pages handle their own permission checks
  
  // If user is not authenticated, redirect to signin
  if (!user) {
    // console.log('ProtectedRoute: User not authenticated, redirecting to signin');
    return <Navigate to="/signin" replace />;
  }

  // SIMPLIFIED: Allow all authenticated users access
  // Let pages handle their own permission checks and show appropriate UI
  // console.log(`✅ ProtectedRoute: Allowing access to authenticated user`);
  
  // Show the protected content
  return <>{children}</>;
}

// Higher-order component for route protection
export function withPermission<T extends object>(
  Component: React.ComponentType<T>,
  requiredPermission: keyof import('../../hooks/usePermissionCheck').UserPermission,
  redirectTo?: string
) {
  return function ProtectedComponent(props: T) {
    return (
      <ProtectedRoute permission={requiredPermission} redirectTo={redirectTo}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
}
